/**
 * Keys that should never participate in change detection.
 * These are almost always transient or contain DOM references/functions.
 */
const EXCLUDED_KEYS = new Set([
  "selectedContent",
  "selectedText",
  "selectedObjects",
  "selectedObject",
  "selectedImage",
  "selectedArrow",
  "selectedTextId",
  "image",
  "originalImage",
  "rotatedOriginalImage",
  "scaledImage",
  "imageNode",
  "imageRef",
  "nodeRef",
  "groupRef",
  "shapeRef",
  "textRef",
  "transformerRef",
  "historySnapshot",
  "history",
  "historyIndex",
  "dragBoundFunc",
  "onDragMove",
  "onDragEnd",
  "onTransform",
  "__proto__",
  "__v",
  "__ob__",
  "listeners",
  "cache",
  "_cache",
  "_id",
  "_reactInternals",
  "tempId", // Temporary IDs used during editing
  "isDragging", // Dragging state
  "isTransforming", // Transforming state
  "canvas", // Canvas reference
  "stage", // Stage reference
  "layer", // Layer reference
  "parent", // Parent reference
  "children", // Children array (can be reordered)
  "index", // Index in parent
  "key", // React key
  "ref", // React ref
  "id", // ID can be reassigned during operations
]);

const isDomNode = (value) => {
  if (typeof window === "undefined") return false;
  if (window.Element && value instanceof window.Element) return true;
  if (typeof Image !== "undefined" && value instanceof Image) return true;
  return false;
};

const sanitizeValue = (value, depth, seen) => {
  if (value === null || value === undefined) return value;

  const valueType = typeof value;
  if (
    valueType === "string" ||
    valueType === "boolean"
  ) {
    return value;
  }

  // Preserve numeric values with full precision for accurate change detection
  if (valueType === "number") {
    // Handle NaN and Infinity cases
    if (Number.isNaN(value)) return NaN;
    if (!Number.isFinite(value)) return value;
    return value; // Return the number as-is to preserve precision
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (isDomNode(value)) {
    // Persist the src when available, otherwise ignore the node entirely
    if (value.src) {
      return value.src;
    }
    return undefined;
  }

  if (seen.has(value)) {
    return undefined;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    const normalizedArray = value
      .map((item) => sanitizeValue(item, depth + 1, seen))
      .filter((item) => item !== undefined);
    return normalizedArray.length > 0 ? normalizedArray : [];
  }

  if (valueType === "object") {
    const normalizedObj = {};

    Object.keys(value).forEach((key) => {
      if (EXCLUDED_KEYS.has(key)) {
        return;
      }

      const normalizedChild = sanitizeValue(value[key], depth + 1, seen);
      if (normalizedChild !== undefined) {
        normalizedObj[key] = normalizedChild;
      }
    });

    // Preserve ids for stable sorting even if they're falsy (0/empty string)
    if (
      (value.id || value.id === 0 || value.id === "") &&
      normalizedObj.id === undefined
    ) {
      normalizedObj.id = value.id;
    } else if (
      (value.shape_id || value.shape_id === 0) &&
      normalizedObj.id === undefined
    ) {
      normalizedObj.id = value.shape_id;
    } else if (
      (value.image_id || value.image_id === 0) &&
      normalizedObj.id === undefined
    ) {
      normalizedObj.id = value.image_id;
    }

    return Object.keys(normalizedObj).length > 0 ? normalizedObj : undefined;
  }

  return undefined;
};

/**
 * Normalizes an element for comparison by removing transient values and DOM refs
 * while retaining every meaningful property for change detection.
 */
const normalizeElement = (element) => {
  if (!element || typeof element !== "object") return element;
  const normalized = sanitizeValue(element, 0, new WeakSet());
  return normalized;
};

/**
 * Deep comparison function to check if two arrays are equal
 * Compares by normalizing elements first to ignore transient differences
 * @param {Array} arr1 - First array
 * @param {Array} arr2 - Second array
 * @param {string} arrayName - Name of the array for debugging (optional)
 * @returns {boolean} True if arrays are equal, false otherwise
 */
export const areArraysEqual = (arr1, arr2, arrayName = "array") => {
  // Handle null/undefined cases
  if (!arr1 && !arr2) return true;
  if (!arr1 || !arr2) return false;

  // Ensure arrays
  const array1 = Array.isArray(arr1) ? arr1 : [];
  const array2 = Array.isArray(arr2) ? arr2 : [];

  // Check length
  if (array1.length !== array2.length) {
    return false;
  }

  // For empty arrays, they're equal
  if (array1.length === 0) return true;

  // Normalize and sort both arrays by id to ensure consistent comparison
  const normalized1 = array1
    .map(normalizeElement)
    .filter((el) => el !== undefined)
    .sort((a, b) => {
      const idA = (a?.id ?? "").toString();
      const idB = (b?.id ?? "").toString();
      return idA.localeCompare(idB);
    });

  const normalized2 = array2
    .map(normalizeElement)
    .filter((el) => el !== undefined)
    .sort((a, b) => {
      const idA = (a?.id ?? "").toString();
      const idB = (b?.id ?? "").toString();
      return idA.localeCompare(idB);
    });

  try {
    // Quick length check first
    if (array1.length !== array2.length) {
      return false;
    }

    // Sort both arrays by ID to ensure consistent comparison
    const sorted1 = [...array1].sort((a, b) => (a.id || "").localeCompare(b.id || ""));
    const sorted2 = [...array2].sort((a, b) => (a.id || "").localeCompare(b.id || ""));

    // Compare element by element
    for (let i = 0; i < sorted1.length; i++) {
      const item1 = sorted1[i];
      const item2 = sorted2[i];

      // Quick ID check
      if (item1.id !== item2.id) {
        return false;
      }

      // Deep comparison of sanitized values
      const sanitized1 = sanitizeValue(item1, 0, new Set());
      const sanitized2 = sanitizeValue(item2, 0, new Set());

      if (JSON.stringify(sanitized1) !== JSON.stringify(sanitized2)) {
        return false;
      }
    }

    return true;
  } catch (error) {
    // If serialization fails (circular references, etc.), fall back to length check
    console.warn(
      "Error comparing arrays, falling back to length check:",
      error
    );
    return array1.length === array2.length;
  }
};

/**
 * Checks if any canvas element arrays have changed from initial state
 * @param {Object} currentState - Current state of all arrays
 * @param {Object} initialState - Initial state of all arrays (after loadCanvasElements)
 * @returns {boolean} True if any arrays have changed, false otherwise
 */
export const hasCanvasArraysChanged = (currentState, initialState) => {
  if (!initialState) {
    // If no initial state recorded, assume no changes (conservative approach)
    return false;
  }

  // Check each array type for changes
  const arraysToCheck = [
    { name: "texts", current: currentState.texts, initial: initialState.texts },
    {
      name: "rectangles",
      current: currentState.rectangles,
      initial: initialState.rectangles,
    },
    {
      name: "circles",
      current: currentState.circles,
      initial: initialState.circles,
    },
    {
      name: "arrows",
      current: currentState.arrows,
      initial: initialState.arrows,
    },
    { name: "lines", current: currentState.lines, initial: initialState.lines },
    {
      name: "doubleSidedArrows",
      current: currentState.doubleSidedArrows,
      initial: initialState.doubleSidedArrows,
    },
    {
      name: "scribbles",
      current: currentState.scribbles,
      initial: initialState.scribbles,
    },
    {
      name: "images",
      current: currentState.images,
      initial: initialState.images,
    },
  ];

  for (const { name, current, initial } of arraysToCheck) {
    const currentArray = Array.isArray(current) ? current : [];
    const initialArray = Array.isArray(initial) ? initial : [];


    if (!areArraysEqual(currentArray, initialArray, name)) {
      return true;
    }
  }

  // No changes detected
  return false;
};
